import { useRef, useLayoutEffect, RefObject } from 'react';
import { animated, useSpring } from 'react-spring';
import { IconCheck } from '@belong/icons';
import { Text, Image } from '@belong/ui';
import clsx from 'clsx';
import { HOMEOWNERS_STRINGS } from 'src/features/homeowners/homeowners.strings';
import css from './less-vacancy-section.module.css';

const onScreenThreshold = 0.25; // Start animation when this % of the section is on screen
const percentageLeft = 0.75 - onScreenThreshold; // Animation will happen on the remaining %

function getRect(ref: RefObject<HTMLDivElement | undefined>) {
  return ref.current
    ? ref.current.getBoundingClientRect()
    : { top: 0, height: 0 };
}

const scrollInterpolationWithFlashing =
  (ref: RefObject<HTMLDivElement | undefined>) => (val: number) => {
    const animationHeight = getRect(ref).height;
    const heightLeft = animationHeight * percentageLeft; // The height over which the animation will happen (40% of height).
    // Result is 0 when val is 0 and goes to 1 when val is heightLeft
    const result = val / heightLeft;

    if (!result || result < 0) return 0;
    if (result >= 1) return 1;

    // Flashing of neon sign as shown in animation
    if (result > 0.8 && result < 0.85) return 0;
    if (result > 0.9 && result < 0.95) return 0;

    return result;
  };

// Animation as shown here: https://principle.app/share?id=zDpinfe5ZMEpkcN9FYSc
export function LessVacancySection() {
  const animationRef: RefObject<HTMLDivElement | undefined> = useRef();
  const [{ sy }, set] = useSpring(() => ({ sy: 0 }));

  useLayoutEffect(() => {
    function handleScroll() {
      const windowHeight = window.innerHeight;
      const { top: animationTop, height: animationHeight } =
        getRect(animationRef);
      const startHeight = animationHeight * onScreenThreshold;
      set({ sy: windowHeight - animationTop - startHeight });
    }

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [set]);

  return (
    <div
      ref={animationRef as RefObject<HTMLDivElement>}
      className={clsx(
        'bg-dark-navy flex w-full flex-col items-center overflow-hidden',
        css.container,
      )}
    >
      <div className={clsx('relative h-full', css.content)}>
        {/* Desktop clouds left */}
        <div
          className={clsx(
            'absolute hidden lg:block xl:left-0',
            css.desktopCloudsLeftContainer,
          )}
        >
          <Image
            src="homeowner-lp/lp_ho_neon_clould_light_left.png"
            alt=""
            className="hidden lg:inline-block"
            config={{ h: 350 }}
          />
          <animated.div
            className="absolute inset-0"
            style={{
              opacity: sy.to(scrollInterpolationWithFlashing(animationRef)),
            }}
          >
            <Image
              src="homeowner-lp/lp_ho_neon_clould_dark_left.png"
              alt=""
              className="hidden lg:inline-block"
              config={{ h: 350 }}
            />
          </animated.div>
        </div>

        {/* Mobile balloons left */}
        <div
          className={clsx(
            'absolute block lg:hidden',
            css.mobileBalloonsLeftContainer,
          )}
        >
          <Image
            src="homeowner-lp/lp_ho_neon_balloon_01_light.png"
            alt=""
            className="inline-block lg:hidden"
            config={{ h: 230 }}
          />
          <animated.div
            className="absolute inset-0"
            style={{
              opacity: sy.to(scrollInterpolationWithFlashing(animationRef)),
            }}
          >
            <Image
              src="homeowner-lp/lp_ho_neon_clould_dark_left.png"
              alt=""
              className="hidden lg:inline-block"
            />
            <Image
              src="homeowner-lp/lp_ho_neon_balloon_01.png"
              alt=""
              className="inline-block lg:hidden"
              config={{ h: 230 }}
            />
          </animated.div>
        </div>

        {/* Desktop clouds right */}
        <div
          className={clsx(
            'absolute hidden lg:block xl:right-0',
            css.desktopCloudsRightContainer,
          )}
        >
          <Image
            src="homeowner-lp/lp_ho_neon_clould_light_right.png"
            alt=""
            className="hidden lg:inline-block"
            config={{ h: 350 }}
          />
          <animated.div
            className="absolute inset-0"
            style={{
              opacity: sy.to(scrollInterpolationWithFlashing(animationRef)),
            }}
          >
            <Image
              src="homeowner-lp/lp_ho_neon_clould_dark_right.png"
              alt=""
              className="hidden lg:inline-block"
              config={{ h: 350 }}
            />
          </animated.div>
        </div>

        {/* Mobile balloons right */}
        <div
          className={clsx(
            'absolute block lg:hidden',
            css.mobileBalloonsRightContainer,
          )}
        >
          <Image
            src="homeowner-lp/lp_ho_neon_balloon_02_light.png"
            alt=""
            className="inline-block lg:hidden"
            config={{ h: 230 }}
          />
          <animated.div
            className="absolute inset-0"
            style={{
              opacity: sy.to(scrollInterpolationWithFlashing(animationRef)),
            }}
          >
            <Image
              src="homeowner-lp/lp_ho_neon_balloon_02.png"
              alt=""
              className="inline-block lg:hidden"
              config={{ h: 230 }}
            />
          </animated.div>
        </div>

        <div className="pb-2xl absolute flex size-full flex-col items-center justify-end lg:flex-row lg:justify-center lg:pb-0">
          <div
            className={clsx(
              'mb-2xl lg:mr-2xl relative flex items-center justify-center lg:mb-0',
              css.neonNumberContainer,
            )}
          >
            <div className="absolute">
              <div className={css.width420}>
                <Image
                  src="homeowner-lp/lp_ho_neon_number_light.png"
                  alt="66% Less Vacancy"
                />
              </div>
            </div>
            <div className="absolute">
              <animated.div
                className={css.width420}
                style={{
                  opacity: sy.to(scrollInterpolationWithFlashing(animationRef)),
                }}
              >
                <Image
                  src="homeowner-lp/lp_ho_neon_number_dark.png"
                  alt=""
                  className="h-auto object-contain"
                />
              </animated.div>
            </div>
          </div>

          <div>
            <div className="mb-xl flex items-start">
              <div className="mr-sm mt-2xs">
                <IconCheck width={20} className="text-green" />
              </div>
              <Text fontWeight="semibold" className={css.bulletText1}>
                {HOMEOWNERS_STRINGS['lessVacancy.daysToLove']}
              </Text>
            </div>
            <div className="mb-xl flex items-start">
              <div className="mr-sm mt-2xs">
                <IconCheck width={20} className="text-green" />
              </div>
              <Text fontWeight="semibold" className={css.bulletText2}>
                {HOMEOWNERS_STRINGS['lessVacancy.qualifiedApplicants']}
              </Text>
            </div>
            <div className="flex items-start">
              <div className="mr-sm mt-2xs">
                <IconCheck width={20} className="text-green" />
              </div>
              <Text fontWeight="semibold" className={css.bulletText3}>
                {HOMEOWNERS_STRINGS['lessVacancy.renews']}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
